import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { XMarkIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import {
  GetAllPacks,
  GetAllModels,
  GenerateLicenses,
  TestAPIConnection,
} from "../api"

// Import step components
import PackSelectionStep from "./steps/PackSelectionStep"
import SettingsStep from "./steps/SettingsStep"
import ReviewStep from "./steps/ReviewStep"

// Import shared components
import StepHeader from "./components/StepHeader"
import StepNavigation from "./components/StepNavigation"

// Import constants
import { INITIAL_FORM_DATA, STEPS } from "./constants"

export default function LicenseGenerator({ onClose }) {
  const [currentStep, setCurrentStep] = useState(1)
  const [dataLoading, setDataLoading] = useState(true)
  const [packs, setPacks] = useState([])
  const [models, setModels] = useState([])
  const [apiError, setApiError] = useState(null)
  const [formData, setFormData] = useState(INITIAL_FORM_DATA)
  const [errors, setErrors] = useState({})

  // Reset function to go back to step 1 with fresh form data
  const resetGenerator = () => {
    setCurrentStep(1)
    setFormData(INITIAL_FORM_DATA)
    setErrors({})
  }

  // Check URL for pre-selected pack
  useEffect(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search)
      const packId = urlParams.get("packId")
      if (packId) {
        setFormData((prev) => ({ ...prev, packId }))
      }
    }
  }, [])

  // Load packs and models
  useEffect(() => {
    async function loadData() {
      try {
        setDataLoading(true)
        setApiError(null)

        // Test API connection first
        const apiWorking = await TestAPIConnection()
        if (!apiWorking) {
          throw new Error(
            "API connection failed - please check backend services"
          )
        }


        const [packsResponse, modelsResponse] = await Promise.all([
          GetAllPacks(),
          GetAllModels(),
        ])


        // Handle packs response
        if (Array.isArray(packsResponse)) {
          const activePacks = packsResponse.filter(
            (pack) => pack.state === "active"
          )
          setPacks(activePacks)
        } else {
          throw new Error("Invalid packs data received from server")
        }

        // Handle models response
        if (Array.isArray(modelsResponse)) {
          setModels(modelsResponse)
        } else {
          throw new Error("Invalid models data received from server")
        }

      } catch (error) {
        setApiError(error.message)
        showNotification({
          message: "Failed to load packs and models: " + error.message,
          className: "bg-admin_error text-white",
        })
      } finally {
        setDataLoading(false)
      }
    }
    loadData()
  }, [])

  // This effect is no longer needed since model selection is now in step 2

  // Handle input changes
  const handleInputChange = (field, value) => {
    // Update form data
    setFormData((prev) => {
      const newFormData = { ...prev, [field]: value }

      // Reset related fields when switching activation code types
      if (field === "activationCodeType") {
        if (value === "auto") {
          // Clear explicit code fields when switching to auto
          newFormData.explicitCodes = ""
          newFormData.customCodesCount = 0
        } else if (value === "explicit") {
          // Clear auto-generation fields when switching to explicit
          newFormData.codeGenerationModelId = ""
        }
      }
      
      return newFormData
    })
    
    // Clear errors for this field and related fields when switching modes
    setErrors((prev) => {
      const newErrors = { ...prev }
      
      // Clear error for the current field
      if (newErrors[field]) {
        delete newErrors[field]
      }
      
      // Clear mode-specific errors when switching activation code types
      if (field === "activationCodeType") {
        if (value === "auto") {
          // Clear explicit code errors
          delete newErrors.explicitCodes
          delete newErrors.customCodesCount
        } else if (value === "explicit") {
          // Clear auto-generation errors
          delete newErrors.count
          delete newErrors.codeGenerationModelId
        }
      }
      
      return newErrors
    })
  }

  // Validate current step
  const validateStep = (step) => {
    const newErrors = {}

    switch (step) {
      case 1:
        if (!formData.packId) {
          newErrors.packId = "Please select a license pack"
        }
        break

      case 2:
        // Validate based on activation code type
        if (formData.activationCodeType === "auto") {
          if (formData.count < 1 || formData.count > 10000) {
            newErrors.count = "Count must be between 1 and 10,000"
          }
          if (!formData.codeGenerationModelId) {
            newErrors.codeGenerationModelId = "Please select a license model"
          }
        } else if (formData.activationCodeType === "explicit") {
          if (formData.customCodesCount < 1) {
            newErrors.explicitCodes = "Please provide at least one activation code"
          }
        }
        
        if (formData.seats < 1) {
          newErrors.seats = "Seats must be at least 1"
        }
        if (
          formData.users_per_license !== undefined &&
          formData.users_per_license !== "" &&
          (isNaN(Number(formData.users_per_license)) || Number(formData.users_per_license) < 0)
        ) {
          newErrors.users_per_license = "Users per License must be 0 (unlimited) or a positive integer"
        }
        if (!formData.productId || !formData.productId.trim()) {
          newErrors.productId = "Product is required"
        }
        if (formData.validityType === "days" && formData.validity < 1) {
          newErrors.validity = "Validity must be at least 1 day"
        }
        break
    }

    setErrors(newErrors)
    const isValid = Object.keys(newErrors).length === 0
    return isValid
  }

  // Handle step navigation
  const handleNext = () => {
    const validationResult = validateStep(currentStep)
    
    if (validationResult) {
      const nextStep = Math.min(currentStep + 1, 3)
      setCurrentStep(nextStep)
    }
  }

  const handlePrevious = () => {
    const prevStep = Math.max(currentStep - 1, 1)
    setCurrentStep(prevStep)
  }

  const handleStepClick = (step) => {
    // Allow going back to completed steps
    if (step <= currentStep) {
      setCurrentStep(step)
    }
  }

  // Generate licenses
  const handleGenerate = async () => {
    if (!validateStep(3)) return

    const selectedPack = packs.find((pack) => pack._id === formData.packId)

    // Use the appropriate count for display
    const licenseCount = formData.activationCodeType === "explicit" 
      ? formData.customCodesCount 
      : parseInt(formData.count)

    // Show loading dialog
    showPromptDialog({
      title: "Generating Licenses",
      message: `Generating ${licenseCount} license${
        licenseCount !== 1 ? "s" : ""
      }...\n\nPlease wait, this may take a few moments.`,
      showYesBtn: false,
      showNoBtn: false,
      onConfirm: () => {},
      onCancel: () => {},
    })

    try {        
      const generateData = {
        packId: formData.packId,
        count: licenseCount,
        seats: parseInt(formData.seats),
        users_per_license: parseInt(formData.users_per_license || 0, 10) || 0,
        productId: formData.productId,
        validity:
          formData.validityType === "lifetime"
            ? 0
            : parseInt(formData.validity),
        supported_platforms: formData.supportedPlatforms || [],
      }

      // Add model if selected for code generation
      if (formData.codeGenerationModelId) {
        generateData.modelId = formData.codeGenerationModelId
      }

      // Add explicit codes if provided
      if (formData.activationCodeType === "explicit") {
        generateData.explicitCodes = formData.explicitCodes
          .split("\n")
          .map((code) => code.trim())
          .filter((code) => code)
      }

      const response = await GenerateLicenses(generateData)

      if (response.result) {
        // Show success prompt dialog (this will auto-close the loading dialog)
        showPromptDialog({
          title: "🎉 Generation Complete!",
          message: `Successfully generated ${
            response.data?.length || licenseCount
          } license${
            (response.data?.length || licenseCount) !== 1 ? "s" : ""
          }.\nThe licenses are now ready for distribution inside ${
            selectedPack?.name || "the selected pack"
          }.`,
          yesLabel: "See Codes",
          noLabel: "Close",
          onConfirm: () => {
            // Navigate to license browser with pack filter using pack name
            const packName = selectedPack?.name
              ? encodeURIComponent(selectedPack.name.replace(/\s+/g, "+"))
              : formData.packId
            window.location.href = `/admin/product-licensing/licenses?pack=${packName}`
          },
          onCancel: () => {
            // Reset generator and return to step 1
            resetGenerator()
          },
        })

      } else {
        // Extract the most informative error message from the response
        const errorMessage = response.message || response.error || "Failed to generate licenses"
        throw new Error(errorMessage)
      }
    } catch (error) {
      // Extract error message from different possible sources
      let errorMessage = "An unexpected error occurred. Please try again."
      
      // Check if it's an axios error with response data
      if (error.response && error.response.data) {
        const responseData = error.response.data
        errorMessage = responseData.message || responseData.error || error.message
      } else if (error.message) {
        errorMessage = error.message
      }
      
      // Show error dialog (this will auto-close the loading dialog)
      showPromptDialog({
        title: "Generation Failed",
        message: errorMessage,
        showYesBtn: true,
        showNoBtn: false,
        yesLabel: "Close",
        onConfirm: () => {
          // Just close the dialog
        },
      })
    }
  }

  // Get selected pack/model info
  const selectedPack = packs.find((pack) => pack._id === formData.packId)
  const selectedModel = models.find((model) => model._id === formData.codeGenerationModelId)

  // Show loading state while fetching data
  if (dataLoading) {
    return (
      <div className="h-full flex flex-col bg-admin_canvas">
        <div className="flex items-center justify-between border-b border-admin_bordered bg-admin_paper p-6">
          <div>
            <Typography 
              component="h1"
              variant="admin_h3"
            >
              License Generator
            </Typography>

            <Typography variant="admin_txt1_dim">
              Generate bulk licenses for distribution
            </Typography>
          </div>

          <Button 
            variant="admin_icon" 
            onClick={onClose}
          >
            <XMarkIcon className="w-6 h-6" />
          </Button>
        </div>

        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-admin_primary mx-auto mb-4"/>

            <Typography variant="admin_body_dim">
              Loading packs and models...
            </Typography>

            <Typography variant="admin_txt2_dim">
              This may take a few seconds
            </Typography>
          </div>
        </div>
      </div>
    )
  }

  // Show error state if API failed
  if (apiError) {
    return (
      <div className="h-full flex flex-col bg-admin_canvas">
        <div className="flex items-center justify-between p-6 border-b border-admin_bordered bg-admin_paper">
          <Typography 
            component="h1"
            variant="admin_h3"
          >
            License Generator
          </Typography>

          <Button 
            variant="admin_icon" 
            onClick={onClose}
          >
            <XMarkIcon className="w-6 h-6" />
          </Button>
        </div>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center max-w-md">
            <ExclamationTriangleIcon className="w-12 h-12 text-admin_error mx-auto mb-4" />

            <Typography 
              variant="admin_h4" 
              className="text-admin_error mb-2"
            >
              Failed to Load Data
            </Typography>

            <Typography variant="admin_body_dim">
              {apiError}
            </Typography>

            <div className="space-x-3">
              <Button
                variant="admin_primary"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>

              <Button 
                variant="admin_secondary" 
                onClick={onClose}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Common props for all step components
  const commonStepProps = {
    formData,
    errors,
    onInputChange: handleInputChange,
    onNext: handleNext,
    onPrevious: handlePrevious,
  }

  return (
    <div className="h-full flex flex-col bg-admin_info_bg">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-admin_bordered bg-admin_paper">
        <div>
          <Typography 
            component="h1"
            variant="admin_h3"
          >
            License Generator{selectedPack ? ` - ${selectedPack.name}` : ""}
          </Typography>

          <Typography variant="admin_txt1_dim">
            Generate bulk licenses for distribution
          </Typography>
        </div>

        <Button variant="admin_secondary" onClick={onClose}>
          Close
        </Button>
      </div>

      {/* Step Indicator */}
      <StepHeader
        steps={STEPS}
        currentStep={currentStep}
        onStepClick={handleStepClick}
      />

      {/* Navigation - Under Step Indicator */}
      <StepNavigation
        currentStep={currentStep}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onGenerate={handleGenerate}
        formData={formData}
        packs={packs}
        models={models}
        errors={errors}
      />

      {/* Scrollable Content */}
      <div className="flex-1 bg-admin_paper shadow-inner">
        <div className="max-w-2xl mx-auto p-6">
          {currentStep === 1 && (
            <PackSelectionStep
              {...commonStepProps}
              packs={packs}
              selectedPack={selectedPack}
            />
          )}

          {currentStep === 2 && (
            <SettingsStep {...commonStepProps} selectedModel={selectedModel} />
          )}

          {currentStep === 3 && (
            <ReviewStep
              formData={formData}
              selectedPack={selectedPack}
              selectedModel={selectedModel}
              models={models}
            />
          )}
        </div>
      </div>
    </div>
  )
}
